import { Avatar, Badge, Box, IconButton, Tooltip, Typography } from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FlagIcon from "@mui/icons-material/Flag";
import HeartBrokenIcon from "@mui/icons-material/HeartBroken";
import HeartBrokenOutlinedIcon from "@mui/icons-material/HeartBrokenOutlined";
import HistoryIcon from "@mui/icons-material/History";
import { FormattedNumber } from "react-intl";
import { getDeviceId } from "utilities";
import { PUBLIC_STATUSES } from "constants";

const getInitials = (name = "") =>
  name.split(" ").filter(Boolean).map((w) => w[0]).join("").toUpperCase().slice(0, 2);

export const ReviewActions = ({
  review,
  user,
  isOwnReview,
  showUserReviewsOnly,
  onLike,
  onDislike,
  onReport,
  onHistory,
  restaurant,
}) => {
  const shouldShowActions = showUserReviewsOnly
    ? isOwnReview
    : PUBLIC_STATUSES.includes(review.status);

  if (!shouldShowActions) return null;

  const deviceId = getDeviceId();
  const isAlreadyReported = review.reportedBy?.some(
    (r) =>
      (r.userId === user?.uid || r.deviceId === deviceId) &&
      r.status !== "rejected",
  );

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Box display="flex" alignItems="center" gap={1}>
        <IconButton size="small" onClick={() => onLike(review.id, user?.uid)} disabled={isOwnReview}>
          {review.likedBy?.includes(user?.uid) ? (
            <FavoriteIcon color="error" />
          ) : (
            <FavoriteBorderIcon />
          )}
        </IconButton>
        {review.likedBy?.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            <FormattedNumber value={review.likedBy.length} notation="compact" maximumFractionDigits={1} />
          </Typography>
        )}
      </Box>

      <Box display="flex" alignItems="center" gap={1}>
        <IconButton size="small" onClick={() => onDislike(review.id, user?.uid)} disabled={isOwnReview}>
          {review.dislikedBy?.includes(user?.uid) ? (
            <HeartBrokenIcon color="error" />
          ) : (
            <HeartBrokenOutlinedIcon />
          )}
        </IconButton>
        {review.dislikedBy?.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            <FormattedNumber value={review.dislikedBy.length} notation="compact" maximumFractionDigits={1} />
          </Typography>
        )}
      </Box>

      <Box display="flex" alignItems="center" gap={1}>
        <IconButton
          size="small"
          onClick={() => onReport(review.id, user?.uid)}
          disabled={isOwnReview || isAlreadyReported}
        >
          <FlagIcon />
        </IconButton>
      </Box>

      {isOwnReview && Array.isArray(review.editHistory) && review.editHistory.length > 0 && (
        <IconButton size="small" onClick={() => onHistory(review)}>
          <HistoryIcon />
        </IconButton>
      )}

      {/* Vendor liked avatar badge */}
      {restaurant?.userId && review.likedBy?.includes(restaurant.userId) && (() => {
        const logoUrl = restaurant?.restaurantLogoUrl ?? review.restaurantLogoUrl ?? null;
        const name = restaurant?.restaurantName ?? review.restaurantName ?? "Restaurant";
        return logoUrl ? (
          <Tooltip title={`${name} liked this review`}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={
                <FavoriteIcon sx={{ fontSize: 10, color: "error.main", bgcolor: "background.paper", borderRadius: "50%" }} />
              }
            >
              <Avatar src={logoUrl} sx={{ width: 20, height: 20, ml: 0.5 }} />
            </Badge>
          </Tooltip>
        ) : (
          <Tooltip title={`${name} liked this review`}>
            <Box sx={{ display: "flex", alignItems: "center", ml: 0.5 }}>
              <Avatar sx={{ width: 18, height: 18, fontSize: 8 }}>
                {getInitials(name)}
              </Avatar>
              <FavoriteIcon sx={{ fontSize: 10, color: "error.main", ml: 0.25 }} />
            </Box>
          </Tooltip>
        );
      })()}

      {/* Vendor disliked avatar badge */}
      {restaurant?.userId && review.dislikedBy?.includes(restaurant.userId) && (() => {
        const logoUrl = restaurant?.restaurantLogoUrl ?? review.restaurantLogoUrl ?? null;
        const name = restaurant?.restaurantName ?? review.restaurantName ?? "Restaurant";
        return logoUrl ? (
          <Tooltip title={`${name} disliked this review`}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={
                <HeartBrokenIcon sx={{ fontSize: 10, color: "error.main", bgcolor: "background.paper", borderRadius: "50%" }} />
              }
            >
              <Avatar src={logoUrl} sx={{ width: 20, height: 20, ml: 0.5 }} />
            </Badge>
          </Tooltip>
        ) : (
          <Tooltip title={`${name} disliked this review`}>
            <Box sx={{ display: "flex", alignItems: "center", ml: 0.5 }}>
              <Avatar sx={{ width: 18, height: 18, fontSize: 8 }}>
                {getInitials(name)}
              </Avatar>
              <HeartBrokenIcon sx={{ fontSize: 10, color: "error.main", ml: 0.25 }} />
            </Box>
          </Tooltip>
        );
      })()}
    </Box>
  );
};